import { isDefined } from '@sama/js-core';
import { omit } from 'lodash';
import { contentUtils2D } from '../../../2d/exported/content-utils-2d';
import { contentUtils3D } from '../../../3d/exported/content-utils-3d';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { getEffectiveItem } from '../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { isPointCloudOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import {
  ContentUtils,
  PartiallyTransformedShape,
} from '../../../team-apis/content/content-utils';
import {
  KeyFrameOperationAttributes,
  KeyLocationOperationAttributes,
  ShapeLocationOperationAttributes,
  ShapeOperationAttributes,
} from '../../../team-apis/otterdam/annotation-operations/shape-operations';
import {
  PointType,
  ShapeLocationExternal,
  ShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { AbstractOutput } from '../../../team-apis/otterdam/outputs/output';
import { InternalToExternalConverter } from './internal-to-external-object-converter';

export class InternalToExternalShapeConverter<
  P extends PointType,
  S extends ShapeType,
> extends InternalToExternalConverter {
  constructor(private contentUtils: ContentUtils<P, S>) {
    super();
  }

  convertToOperationAttributes(
    shape: ShapeState<P, S>,
  ): ShapeOperationAttributes {
    const { type, tags } = this.contentUtils.convertShapeStateToAnswer(shape);
    return { type, tags };
  }

  convertToLocationOperationAttributes(
    shapeState: ShapeState<P, S>,
    frameNumber: number,
  ) {
    const transformedShape =
      this.contentUtils.convertShapeStateToAnswer(shapeState);

    const keyLocationAttributes = this.convertShapeLocationToExternal(
      transformedShape,
      frameNumber,
    );
    const keyFrameAttributes = this.convertDynamicTagsToExternal(
      transformedShape,
      frameNumber,
    );

    return this.buildLocationAttributes(
      keyLocationAttributes,
      keyFrameAttributes,
    );
  }

  private buildLocationAttributes(
    shapeLocation?: ShapeLocationExternal<P>,
    tagAttributes?: KeyFrameOperationAttributes,
  ): ShapeLocationOperationAttributes | undefined {
    if (shapeLocation && tagAttributes) {
      return {
        ...this.buildKeyLocationAttributes(shapeLocation),
        tags: tagAttributes.tags,
      };
    } else if (shapeLocation) {
      return this.buildKeyLocationAttributes(shapeLocation);
    }

    return tagAttributes;
  }

  private convertShapeLocationToExternal(
    shapeState: PartiallyTransformedShape<P, S>,
    frameNumber: number,
  ): ShapeLocationExternal<P> | undefined {
    const shapeLocation = getEffectiveItem(
      shapeState.key_locations,
      frameNumber,
    );
    if (!shapeLocation) {
      return undefined;
    }

    if (shapeLocation.frame_number < frameNumber) {
      const points =
        shapeLocation.extrapolation?.points[
          frameNumber - shapeLocation.frame_number - 1
        ];
      if (!points) {
        return undefined;
      }

      return {
        points,
        frame_number: frameNumber,
        visibility: shapeLocation.visibility,
      };
    } else if (shapeLocation.frame_number > frameNumber) {
      return undefined;
    }

    // Source is omited until we support MLE for objects
    return omit(shapeLocation, 'extrapolation', 'source');
  }

  private buildKeyLocationAttributes(shapeLocation: ShapeLocationExternal<P>) {
    const operationAttributes: KeyLocationOperationAttributes = {
      visibility: shapeLocation.visibility,
      points: shapeLocation.points,
    };

    if (isDefined(shapeLocation.source)) {
      operationAttributes.source = shapeLocation.source;
    }
    return operationAttributes;
  }
}

export const internalToExternalConverter3D =
  new InternalToExternalShapeConverter(contentUtils3D);

export const internalToExternalConverter2D =
  new InternalToExternalShapeConverter(contentUtils2D);

export function getInternalToExternalConverter(
  output: AbstractOutput,
): InternalToExternalShapeConverter<PointType, ShapeType> {
  if (isPointCloudOutput(output)) {
    return internalToExternalConverter3D;
  } else {
    return internalToExternalConverter2D;
  }
}
